import React from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../redux/store";
import { NavOrigin } from "../../../../../shared-logic/utils/Navigation";
import { BnbLabel } from "../../../../utils/labels";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import * as FinanceStyles from "./styles/Finance";
import * as FinanceInsuranceCtaStyles from "./styles/FinanceInsuranceCtaStyles";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../../common-deprecated/utils/modalConstants";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import MonthlyPrice from "../../common/price/MonthlyPrice";
import useFinanceToggle from "../../../../hooks/shared/actions/useFinanceToggle";
import InsurancePrice from "../../common/price/InsurancePrice";
import { hasInsuranceConfigured } from "../../../../utils/financeInsuranceUtils";
import SrOnly from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/ScreenreaderOnly";
import { useFinanceInsurance, useFinanceInsuranceNavigation } from "../../../../hooks/shared/useFinanceInsurance";
import Spacer from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/Spacer";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

const FinanceInsuranceCta = (): JSX.Element | null => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);

    const { isPersonalizedQuote, info: insuranceInfo } = useBnbSelector((state) => state.insuranceRate);

    const step = useBnbSelector((state) => state.navigation.step);

    const { showMonthlyLoader } = useFinanceToggle();

    const { showFinance, showInsurance, monthlyFormat, totalPrice, financeInfo } = useFinanceInsurance();

    const { onClick: onCtaClick, financeLabelKey, insuranceLabelKey } = useFinanceInsuranceNavigation(step);

    return (
        <Spacer mb={5}>
            <FinanceStyles.ButtonGroup>
                {showFinance && (
                    <FinanceInsuranceCtaStyles.Button
                        onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                            onCtaClick(event, "cc-action", NavOrigin.FinancialCta);
                        }}
                    >
                        <IconWrapper>
                            <Icon variant="card" size="medium" />
                        </IconWrapper>
                        <FinanceInsuranceCtaStyles.ButtonContent>
                            <FinanceInsuranceCtaStyles.ButtonTitle>
                                <BnbLabel label={financeLabelKey} />
                            </FinanceInsuranceCtaStyles.ButtonTitle>
                        </FinanceInsuranceCtaStyles.ButtonContent>
                        <IconWrapper>
                            <Icon variant="arrowRight" />
                        </IconWrapper>
                    </FinanceInsuranceCtaStyles.Button>
                )}
                {showInsurance && (
                    <FinanceInsuranceCtaStyles.Button
                        onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                            onCtaClick(event, "cc-action", NavOrigin.InsuranceCta);
                        }}
                    >
                        <IconWrapper>
                            <Icon variant="insurance" size="medium" />
                        </IconWrapper>
                        <FinanceInsuranceCtaStyles.ButtonContent>
                            <FinanceInsuranceCtaStyles.ButtonTitle>
                                <BnbLabel label={insuranceLabelKey} />
                            </FinanceInsuranceCtaStyles.ButtonTitle>
                        </FinanceInsuranceCtaStyles.ButtonContent>
                        <IconWrapper>
                            <Icon variant="arrowRight" />
                        </IconWrapper>
                    </FinanceInsuranceCtaStyles.Button>
                )}
            </FinanceStyles.ButtonGroup>
            <FinanceStyles.ButtonGroup hideOnMobile>
                {showFinance && totalPrice.monthly !== 0 && (
                    <FinanceInsuranceCtaStyles.FinanceDisclaimerContent>
                        <MonthlyPrice
                            price={totalPrice}
                            priceFormat={monthlyFormat}
                            showLoader={showMonthlyLoader}
                            forceHideDisclaimer
                            financeInfo={financeInfo}
                            size="small"
                        />
                        {financeInfo && financeInfo.disclaimer && (
                            <Button.Link
                                type="button"
                                hasIconRight
                                onClick={(event) => {
                                    event.stopPropagation();
                                    dispatch(
                                        showModal({
                                            type: MODAL_FINANCE_DISCLAIMER,
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            disclaimer: financeInfo.disclaimer!,
                                            productName: "",
                                        }),
                                    );
                                }}
                            >
                                <IconWrapper>
                                    <Icon variant="info" />
                                </IconWrapper>
                                <SrOnly>
                                    <BnbLabel label="bnbA11yButtonMoreInfo" />
                                </SrOnly>
                            </Button.Link>
                        )}
                    </FinanceInsuranceCtaStyles.FinanceDisclaimerContent>
                )}
                {showInsurance && hasInsuranceConfigured(bnbSettings.requireDefaultInsurance, isPersonalizedQuote) && (
                    <FinanceInsuranceCtaStyles.FinanceDisclaimerContent>
                        <InsurancePrice forceHideDisclaimer />
                        {insuranceInfo && insuranceInfo.disclaimer && (
                            <Button.Link
                                type="button"
                                hasIconRight
                                onClick={(event) => {
                                    event.stopPropagation();
                                    dispatch(
                                        showModal({
                                            type: MODAL_FINANCE_DISCLAIMER,
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            disclaimer: insuranceInfo.disclaimer!,
                                            productName: "",
                                        }),
                                    );
                                }}
                            >
                                <IconWrapper>
                                    <Icon variant="info" />
                                </IconWrapper>
                                <SrOnly>
                                    <BnbLabel label="bnbA11yButtonMoreInfo" />
                                </SrOnly>
                            </Button.Link>
                        )}
                    </FinanceInsuranceCtaStyles.FinanceDisclaimerContent>
                )}
            </FinanceStyles.ButtonGroup>
        </Spacer>
    );
};

export default FinanceInsuranceCta;
